import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { createDischarge } from "../../services/DischargeService";
import { updateStatusById } from "../../services/PatientAdmissionService";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService";
import { getDoctorById } from "../../services/doctorService";
import IPDDischargeBilling from "../../components/IPDDischargeBilling";

const validationSchema = Yup.object({
  treatingDoctor: Yup.string().required("Treating Doctor is required"),
});

const DischargeForm = ({
  open,
  onClose,
  admissionId,
  patientId,
  patient_info = {},
  refreshData,
}) => {
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [showBilling, setShowBilling] = useState(false);
  const [billGenerated, setBillGenerated] = useState(false);

  useEffect(() => {
    if (open && admissionId) {
      fetchAssignmentAdmission(admissionId);
    }
    // reset final bill state when modal opens
    if (open) setBillGenerated(false);
  }, [open, admissionId]);

  // const fetchAssignmentAdmission = async (admissionId) => {
  //   try {
  //     const res = await getAssignmentByAdmissionId(admissionId);
  //     const doctorMap = new Map();

  //     res.forEach((item) => {
  //       if (item.doctorId && item.doctorName) {
  //         doctorMap.set(item.doctorId, item.doctorName);
  //       }
  //     });

  //     const doctorList = Array.from(doctorMap, ([id, name]) => ({
  //       id,
  //       name,
  //     }));
  //     setAssignedDoctors(doctorList);
  //   } catch (error) {
  //     console.error("Failed to fetch assignments", error);
  //     setAssignedDoctors([]);
  //   }
  // };

  const fetchAssignmentAdmission = async (admissionId) => {
    try {
      const res = await getAssignmentByAdmissionId(admissionId);
      console.log('[DischargeForm] Assignment received:', res);

      // Handle array response (get latest assignment)
      let assignmentData = res;
      if (Array.isArray(res) && res.length > 0) {
        // Sort by id (higher id = more recent) or createdAt
        const sorted = [...res].sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return (b.id || 0) - (a.id || 0);
        });
        assignmentData = sorted[0];
      }

      if (assignmentData && assignmentData.doctorId) {
        // Try to get doctor name from assignment first
        let doctorName = assignmentData.doctorName;
        
        // If doctor name not available (null, undefined, empty string, or 'N/A'), fetch it
        if (!doctorName || doctorName === 'N/A' || doctorName === 'null' || doctorName === null) {
          console.log('[DischargeForm] Doctor name not available in assignment, fetching doctor by ID:', assignmentData.doctorId);
          try {
            const doctor = await getDoctorById(assignmentData.doctorId);
            console.log('[DischargeForm] Doctor fetched:', doctor);
            doctorName = doctor?.name || doctor?.fullName || `Dr. ID: ${assignmentData.doctorId}`;
            console.log('[DischargeForm] Resolved doctor name:', doctorName);
          } catch (doctorError) {
            console.error('[DischargeForm] Error fetching doctor:', doctorError);
            doctorName = `Dr. ID: ${assignmentData.doctorId}`;
          }
        } else {
          console.log('[DischargeForm] Using doctor name from assignment:', doctorName);
        }
        
        const doctorInfo = { id: assignmentData.doctorId, name: doctorName };
        setAssignedDoctors([doctorInfo]);
      } else {
        console.log('[DischargeForm] No doctorId in assignment');
        setAssignedDoctors([]);
      }

    } catch (error) {
      console.error("Failed to fetch assignments", error);
      setAssignedDoctors([]);
    }
  };
  
  const initialValues = useMemo(() => ({
    dischargeDate: dayjs().format("YYYY-MM-DD"),
    dischargeTime: dayjs().format("HH:mm"),
    dischargeType: "",
    finalDiagnosis: "",
    conditionOnDischarge: "",
    followUpAdvice: "",
    treatingDoctor: "",
    remarks: "",
  }), [admissionId, patientId]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          admissionId,
          patientId,
          doctorId: values.treatingDoctor,
          dischargeDate: values.dischargeDate,
          dischargeTime: values.dischargeTime,
          dischargeType: (values.dischargeType || "").toUpperCase(),
          finalDiagnose: values.finalDiagnosis,
          followUpAdvice: values.followUpAdvice,
          conditionOnDischarge: values.conditionOnDischarge,
          remarks: values.remarks,
        };

        await createDischarge(payload);
        
        // Try to update admission status, but handle transaction conflicts gracefully
        // The discharge creation might already update the status on the backend
        try {
          await updateStatusById(admissionId, { status: "discharge" });
        } catch (statusError) {
          // If it's a transaction conflict (row was updated by another transaction),
          // the discharge creation likely already updated the status, so we can continue
          const errorMessage = statusError?.response?.data?.message || statusError?.message || '';
          if (errorMessage.includes('updated or deleted by another transaction') || 
              errorMessage.includes('unsaved-value mapping was incorrect')) {
            console.log('[DischargeForm] Admission status update conflict - discharge creation likely already updated status');
            // Continue with success flow
          } else {
            // Re-throw if it's a different error
            throw statusError;
          }
        }

        alert("Discharge formalities submitted successfully!");
        resetForm();
        
        // Refresh table data if callback provided - force refresh to get latest status
        if (refreshData) {
          // Pass true to force refresh and bypass cache
          refreshData(true);
        }
        
        onClose();
      } catch (error) {
        console.error("Error saving discharge formalities:", error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        alert("Submission failed: " + errorMessage);
      }
    },
  });

  // Auto-populate treating doctor field when assignedDoctors is set
  useEffect(() => {
    if (assignedDoctors.length > 0 && !formik.values.treatingDoctor) {
      const doctorId = assignedDoctors[0].id.toString();
      console.log('[DischargeForm] Auto-populating treating doctor field:', doctorId, 'Doctor name:', assignedDoctors[0].name);
      formik.setFieldValue('treatingDoctor', doctorId);
    }
  }, [assignedDoctors]); // eslint-disable-line react-hooks/exhaustive-deps

  const dischargeTypes = [
    { label: "Planned", value: "PLANNED" },
    { label: "LAMA", value: "LAMA" },
    { label: "Absconded", value: "ABSCONDED" },
    { label: "Death", value: "DEATH" },
    { label: "Emergency", value: "EMERGENCY" },
  ];

  return (
    <BasicModal open={open} onClose={onClose} sx={COMPACT_FORM_MODAL_SX}>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center" }}
          gutterBottom
        >
          Discharge Formalities
        </Typography>
        {/* Final Bill trigger moved to top */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setShowBilling((v) => !v)}
            disabled={billGenerated}
          >
            {billGenerated ? "Final Bill Generated" : showBilling ? "Hide Final Bill" : "Generate Final Bill (Tariff)"}
          </Button>
          {showBilling && (
            <Box sx={{ mt: 2, width: '100%' }}>
              <IPDDischargeBilling
                key={`billing-${admissionId}`}
                admissionId={admissionId}
                patientId={patientId}
                patientName={patient_info?.name || "Patient"}
                onClose={() => setShowBilling(false)}
                onBillGenerated={() => { setBillGenerated(true); setShowBilling(false); }}
              />
            </Box>
          )}
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Discharge Date"
                name="dischargeDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.dischargeDate}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Discharge Time"
                name="dischargeTime"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={formik.values.dischargeTime}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Discharge Type"
                name="dischargeType"
                value={formik.values.dischargeType}
                onChange={formik.handleChange}
              >
                {dischargeTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Final Diagnosis"
                name="finalDiagnosis"
                value={formik.values.finalDiagnosis}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Condition on Discharge"
                name="conditionOnDischarge"
                value={formik.values.conditionOnDischarge}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Follow-up Advice"
                name="followUpAdvice"
                value={formik.values.followUpAdvice}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={
                  formik.touched.treatingDoctor &&
                  Boolean(formik.errors.treatingDoctor)
                }
              >
                <InputLabel>Treating Doctor</InputLabel>
                <Select
                  name="treatingDoctor"
                  value={formik.values.treatingDoctor}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Treating Doctor"
                >
                  {assignedDoctors.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.treatingDoctor &&
                    formik.errors.treatingDoctor}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (optional)"
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={onClose}
                submitType="submit"
                submitLabel="Submit Discharge"
                submitDisabled={!billGenerated}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
      {/* Final Bill (Tariff) section moved above */}
    </BasicModal>
  );
};

export default DischargeForm;